import React, { useState, Fragment } from 'react';
import { Dialog, Transition, Disclosure } from '@headlessui/react';
import {
  XMarkIcon,
  UserGroupIcon,
  AcademicCapIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  ChevronUpIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { useDistributeFormMutation, useGetClassesQuery } from '../../store/api/localApi';
import { FormType } from '../../types';
import StudentPicker from './StudentPicker';

interface SendFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  formId: string;
  formTitle: string;
  formType: FormType;
  locationId: string;
}

type TargetType = 'LOCATION' | 'CLASS' | 'SPECIFIC_STUDENTS';

const SendFormModal: React.FC<SendFormModalProps> = ({
  isOpen,
  onClose,
  formId,
  formTitle,
  formType,
  locationId,
}) => {
  // CONSENT sends carry an event context (name/date/description); STUDENT_INTAKE
  // sends do not — the form is the same for every child, no event involved.
  const isConsent = formType === FormType.CONSENT;
  const [targetType, setTargetType] = useState<TargetType>('LOCATION');
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [isBlocking, setIsBlocking] = useState(false);
  const [deadline, setDeadline] = useState<string>('');
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    created: number;
    skipped: number;
    errors: number;
  } | null>(null);

  const { data: classes, isLoading: isLoadingClasses } = useGetClassesQuery(locationId, {
    skip: !isOpen || targetType !== 'CLASS',
  });

  const [distribute, { isLoading: isDistributing }] = useDistributeFormMutation();

  const handleSend = async () => {
    setError(null);
    setSuccess(null);

    if (isConsent && !eventName.trim()) {
      setError('Event name is required');
      return;
    }
    if (targetType === 'CLASS' && !selectedClassId) {
      setError('Please select a class');
      return;
    }
    if (targetType === 'SPECIFIC_STUDENTS' && selectedStudentIds.length === 0) {
      setError('Please select at least one student');
      return;
    }

    try {
      const payload: Parameters<typeof distribute>[0] = {
        formId,
        locationId,
        targetType,
        isBlocking,
      };

      if (targetType === 'CLASS') {
        payload.targetId = selectedClassId;
      }
      if (targetType === 'SPECIFIC_STUDENTS') {
        payload.studentIds = selectedStudentIds;
      }
      // Event context applies only to CONSENT; STUDENT_INTAKE omits it entirely.
      if (isConsent) {
        payload.eventName = eventName.trim();
        if (eventDate) payload.eventDate = eventDate;
        if (eventDescription.trim()) payload.eventDescription = eventDescription.trim();
      }
      if (deadline) payload.deadline = deadline;

      const result = await distribute(payload).unwrap();

      setSuccess({
        created: result.created,
        skipped: result.skipped,
        errors: result.errors,
      });
    } catch (err: any) {
      setError(err.data?.error || 'Failed to send form. Please try again.');
    }
  };

  const handleClose = () => {
    setSelectedStudentIds([]);
    setSelectedClassId('');
    setIsBlocking(false);
    setDeadline('');
    setEventName('');
    setEventDate('');
    setEventDescription('');
    setError(null);
    setSuccess(null);
    setTargetType('LOCATION');
    onClose();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={handleClose}
        data-testid="distribute-modal"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                data-testid={`send-form-modal-form-type-${formType}`}
                className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full max-w-sm sm:max-w-2xl sm:p-6"
              >
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title
                      as="h3"
                      data-testid={`send-form-modal-title-${formType}`}
                      className="text-lg font-semibold leading-6 text-gray-900"
                    >
                      {isConsent ? 'Send consent form' : 'Send intake form'}
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">
                      Distribute &ldquo;{formTitle}&rdquo; to recipients
                    </p>

                    {/* Success Message */}
                    {success && (
                      <div className="mt-4 rounded-md bg-green-50 p-4">
                        <div className="flex">
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-green-800">
                              Form sent successfully!
                            </h3>
                            <div
                              data-testid="send-form-result-summary"
                              className="mt-2 text-sm text-green-700"
                            >
                              <ul className="list-disc list-inside">
                                <li>{success.created} created</li>
                                <li>{success.skipped} skipped (already invited)</li>
                                {success.errors > 0 && <li>{success.errors} error(s)</li>}
                              </ul>
                            </div>
                            <div className="mt-4">
                              <button
                                type="button"
                                onClick={handleClose}
                                className="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
                              >
                                Done
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {!success && (
                      <>
                        {/* Step 1: Target Type Selection */}
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Step 1: Select recipients
                          </label>
                          <div className="space-y-2">
                            <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                              <input
                                type="radio"
                                name="targetType"
                                value="LOCATION"
                                data-testid="send-form-target-LOCATION"
                                checked={targetType === 'LOCATION'}
                                onChange={() => setTargetType('LOCATION')}
                                className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                              />
                              <MapPinIcon className="h-5 w-5 ml-3 mr-2 text-gray-400" />
                              <span className="text-sm text-gray-700">Entire Location</span>
                            </label>
                            <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                              <input
                                type="radio"
                                name="targetType"
                                value="CLASS"
                                data-testid="send-form-target-CLASS"
                                checked={targetType === 'CLASS'}
                                onChange={() => setTargetType('CLASS')}
                                className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                              />
                              <UserGroupIcon className="h-5 w-5 ml-3 mr-2 text-gray-400" />
                              <span className="text-sm text-gray-700">Specific Class</span>
                            </label>
                            <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                              <input
                                type="radio"
                                name="targetType"
                                value="SPECIFIC_STUDENTS"
                                data-testid="send-form-target-SPECIFIC_STUDENTS"
                                checked={targetType === 'SPECIFIC_STUDENTS'}
                                onChange={() => setTargetType('SPECIFIC_STUDENTS')}
                                className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                              />
                              <AcademicCapIcon className="h-5 w-5 ml-3 mr-2 text-gray-400" />
                              <span className="text-sm text-gray-700">Specific Students</span>
                            </label>
                          </div>
                        </div>

                        {/* Step 2: Class Picker or Student Multi-Select */}
                        {targetType === 'CLASS' && (
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Step 2: Select class
                            </label>
                            {isLoadingClasses ? (
                              <p className="text-sm text-gray-500">Loading classes...</p>
                            ) : !classes || classes.length === 0 ? (
                              <p className="text-sm text-gray-500 italic">
                                No classes available for this location
                              </p>
                            ) : (
                              <select
                                value={selectedClassId}
                                onChange={(e) => setSelectedClassId(e.target.value)}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              >
                                <option value="">Select a class...</option>
                                {classes.map((cls) => (
                                  <option key={cls.id} value={cls.id}>
                                    {cls.name}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        )}

                        {targetType === 'SPECIFIC_STUDENTS' && (
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Step 2: Select students
                            </label>
                            <StudentPicker
                              locationId={locationId}
                              selectedStudentIds={selectedStudentIds}
                              onChange={setSelectedStudentIds}
                            />
                          </div>
                        )}

                        {/* Event context — CONSENT only. STUDENT_INTAKE has no
                            event, so the whole block is hidden and omitted from
                            the payload. */}
                        {isConsent && (
                          <>
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Event Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                data-testid="send-event-name"
                                value={eventName}
                                onChange={(e) => setEventName(e.target.value)}
                                placeholder="e.g. Dublin Zoo Trip"
                                required
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              />
                            </div>

                            {/* Optional event metadata */}
                            <div className="mt-3">
                              <Disclosure>
                                {({ open }) => (
                                  <>
                                    <Disclosure.Button className="flex w-full items-center justify-between rounded-lg bg-gray-50 px-4 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-100">
                                      <div className="flex items-center">
                                        <CalendarDaysIcon className="h-5 w-5 mr-2 text-gray-400" />
                                        Date & description (optional)
                                      </div>
                                      <ChevronUpIcon
                                        className={`${
                                          open ? 'rotate-180 transform' : ''
                                        } h-5 w-5 text-gray-500 transition-transform`}
                                      />
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="px-1 pt-3 pb-1 space-y-3">
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Event Date
                                        </label>
                                        <input
                                          type="date"
                                          value={eventDate}
                                          onChange={(e) => setEventDate(e.target.value)}
                                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Event Description
                                        </label>
                                        <textarea
                                          value={eventDescription}
                                          onChange={(e) => setEventDescription(e.target.value)}
                                          rows={2}
                                          placeholder="Additional details about the event..."
                                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                        />
                                      </div>
                                    </Disclosure.Panel>
                                  </>
                                )}
                              </Disclosure>
                            </div>
                          </>
                        )}

                        {/* Step 4: Options */}
                        <div className="mt-4 space-y-3">
                          <label className="block text-sm font-medium text-gray-700">Options</label>

                          {/* Deadline */}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Deadline (optional)
                            </label>
                            <input
                              type="date"
                              value={deadline}
                              onChange={(e) => setDeadline(e.target.value)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>

                          {/* Blocking Toggle */}
                          <div className="flex items-start">
                            <input
                              type="checkbox"
                              checked={isBlocking}
                              onChange={(e) => setIsBlocking(e.target.checked)}
                              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <div className="ml-3">
                              <label className="text-sm font-medium text-gray-700">
                                Block attendance until completed
                              </label>
                              <p className="text-xs text-gray-500">
                                Parents must complete this form before accessing their child's
                                features
                              </p>
                            </div>
                          </div>
                          {isBlocking && (
                            <div className="flex items-start p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                              <p className="ml-2 text-xs text-yellow-700">
                                Blocking forms will prevent parents from accessing certain child
                                features until completed.
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Error Message */}
                        {error && (
                          <div className="mt-4 rounded-md bg-red-50 p-3">
                            <p className="text-sm text-red-700">{error}</p>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse gap-3">
                          <button
                            type="button"
                            data-testid="send-form-submit"
                            onClick={handleSend}
                            disabled={isDistributing}
                            className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50 sm:w-auto"
                          >
                            {isDistributing ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Sending...
                              </>
                            ) : (
                              'Send Form'
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={handleClose}
                            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SendFormModal;
